import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { UserCheck, Ticket, CalendarCheck2, Circle } from 'lucide-react';
import { dashboardApi } from '@/lib/api';
import type { ReceptionistDashboardData, DoctorChamber } from './types';
import {
  MetricCard, MetricsGrid, DashboardShell, LoadingState, ErrorState,
} from './_shared';

const STATUS_STYLE: Record<DoctorChamber['status'], { dot: string; label: string; card: string }> = {
  active:      { dot: 'bg-emerald-500', label: 'Active',      card: '' },
  break:       { dot: 'bg-amber-500',   label: 'On Break',    card: 'opacity-75' },
  unavailable: { dot: 'bg-gray-300',    label: 'Unavailable', card: 'opacity-50' },
};

function ChamberCard({ chamber }: { chamber: DoctorChamber }) {
  const s = STATUS_STYLE[chamber.status];
  const pct = chamber.totalTokens ? Math.round((chamber.currentToken / chamber.totalTokens) * 100) : 0;
  const barColor = chamber.status !== 'active' ? '#d1d5db' : pct >= 80 ? '#ef4444' : pct >= 60 ? '#f59e0b' : '#10b981';

  return (
    <div className={clsx('bg-white rounded-2xl border border-gray-100 shadow-card p-5', s.card)}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p className="font-semibold text-navy text-sm">{chamber.doctorName}</p>
          <p className="text-xs text-gray-400 mt-0.5">{chamber.specialization}</p>
        </div>
        <span className={clsx('flex items-center gap-1.5 text-2xs font-semibold px-2 py-0.5 rounded-full border',
          chamber.status === 'active'      ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
          chamber.status === 'break'       ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                             'bg-gray-100 text-gray-500 border-gray-200',
        )}>
          <Circle size={6} className={clsx('fill-current', s.dot.replace('bg-', 'text-'))} fill="currentColor"/>
          {s.label}
        </span>
      </div>

      {/* Token progress */}
      <div className="flex items-end justify-between mb-2">
        <div>
          <span className="text-3xl font-bold text-navy leading-none">{chamber.currentToken}</span>
          <span className="text-xs text-gray-400 ml-1">/ {chamber.totalTokens} tokens</span>
        </div>
        {chamber.status === 'active' && (
          <p className="text-xs text-gray-400">~{chamber.avgWaitMinutes}m wait</p>
        )}
      </div>

      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }}/>
      </div>
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────
export default function ReceptionistDashboard() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery<ReceptionistDashboardData>({
    queryKey:        ['dashboard-receptionist'],
    queryFn:         () => dashboardApi.receptionist().then(r => r.data),
    staleTime:       60_000,
    refetchInterval: 2 * 60_000,
  });

  if (isLoading) return <LoadingState/>;
  if (isError || !data) return <ErrorState onRetry={() => refetch()}/>;

  return (
    <DashboardShell
      title="Reception Dashboard"
      subtitle="Live OPD queue status across all chambers"
      onRefresh={() => refetch()}
      refreshing={isFetching}>

      <MetricsGrid>
        <MetricCard icon={UserCheck}     label="Today's Footfall"  value={data.todayFootfall}   accent="bg-orange/15"/>
        <MetricCard icon={Ticket}        label="Live Tokens"        value={data.liveTokenCount}  accent="bg-blue-50"/>
        <MetricCard icon={CalendarCheck2} label="Available Slots"  value={data.availableSlots}  accent="bg-emerald-50"/>
        <MetricCard
          icon={Circle}
          label="Active Chambers"
          value={data.chambers.filter(c => c.status === 'active').length}
          accent="bg-violet-50"
        />
      </MetricsGrid>

      {/* Doctor Chambers Grid */}
      <div>
        <h2 className="text-sm font-semibold text-navy mb-3">Doctor Chambers — Live Status</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.chambers.map(c => <ChamberCard key={c.doctorId} chamber={c}/>)}
        </div>
      </div>

    </DashboardShell>
  );
}
